import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { FinalcutProgressData, FinalcutQueueData } from 'services/sse.service';
import { anyProcessingFinalCuts as anyProcessingFinalCutsService } from 'services/finalcut.service';
import { RootState } from 'store/store';
import { createSafeAsyncThunk } from 'store/store.helper';
import { isFcStatusCompleted } from 'components/app/_helpers/_app.helper';
import { fetchStorageSize, fetchFinalcuts } from 'components/app/_mediator/app-data.slice';
import { fetchFinalCut } from './fc-data.slice';

interface State {
  fcProgress: Record<FinalcutId, number>;
  fcQueue: Record<FinalcutId, number>;
}

const initialState: State = {
  fcProgress: {},
  fcQueue: {},
};

// T H U N K S

const SLICE_ID = 'fc__renderServer';

export const handleProcessingFc = createSafeAsyncThunk(
  SLICE_ID + '/handleProcessingFc',
  async (payload: FinalcutProgressData, { getState, dispatch }) => {
    const { finalCutId, status, percent, errorMessage } = payload;
    const state = getState() as RootState;
    const { selectedFinalcut, selectedStory } = state.projects;
    const { finalcuts } = state.app__data;

    if (percent) {
      dispatch(slice.actions.setFinalcutProgress({ finalCutId, value: percent }));
      dispatch(slice.actions.clearFinalcutQueue(finalCutId));
    }
    if (status && isFcStatusCompleted(status)) {
      dispatch(fetchStorageSize());
      const isSelectedStory = !!finalcuts.find((el) => el.id === finalCutId);
      if (isSelectedStory) dispatch(fetchFinalcuts(selectedStory!));
      if (errorMessage) toast.error(`Finalcut rendering error: ${payload.errorMessage}`);
      if (selectedFinalcut === finalCutId) dispatch(fetchFinalCut(finalCutId));
      if (!(await dispatch(anyProcessingFinalCuts()).unwrap())) return { closeSse: true };
    }
  }
);

export const anyProcessingFinalCuts = createSafeAsyncThunk(SLICE_ID + '/anyProcessingFinalCuts', async () => {
  const res = await anyProcessingFinalCutsService();
  return res.data;
});

// S L I C E

export const slice = createSlice({
  name: SLICE_ID,
  initialState,
  reducers: {
    setFinalcutQueue: (state, action: PayloadAction<FinalcutQueueData[]>) => {
      const payload: State['fcQueue'] = {};
      action.payload.forEach((data) => {
        payload[data.finalCutId] = data.positionInQueue;
      });
      state.fcQueue = payload;
    },
    setFinalcutProgress: (state, action: PayloadAction<{ finalCutId: FinalcutId; value: number }>) => {
      const { finalCutId, value } = action.payload;
      state.fcProgress[finalCutId] = value;
    },
    deleteProcessingFc: (state, action: PayloadAction<FinalcutId>) => {
      delete state.fcProgress[action.payload];
    },
    clearFinalcutQueue: (state, action: PayloadAction<FinalcutId>) => {
      delete state.fcQueue[action.payload];
    },
  },
});

export const { setFinalcutQueue, deleteProcessingFc, clearFinalcutQueue } = slice.actions;
export default slice.reducer;
